import React from 'react';
import { DollarSignIcon, AlertTriangleIcon } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { SectionIntro } from '../SectionIntro';
import { formatInt, cleanInt, handleIntInputFocus, handleIntInputBlur } from '../../utils/formatters';
export const ChannelModelFit = ({
  arpu,
  updateSimulation,
  cac,
  ltvCacRatio
}) => {
  const handleArpuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = cleanInt(e.target.value);
    if (cleaned === '') {
      updateSimulation({
        arpu: 0
      });
      return;
    }
    const value = parseInt(cleaned);
    if (!isNaN(value)) {
      updateSimulation({
        arpu: value
      });
    }
  };
  return <div className="space-y-6" data-id="element-79">
      <SectionIntro title="Channel ↔ Model Fit" description="Your pricing must support your acquisition channel. Expensive channels need high revenue per customer. Low-cost channels can work with lower pricing. This is the economics of growth." whyItMatters="If your Customer Acquisition Cost (CAC) is higher than your revenue per customer (ARPU), you're burning money on every customer. The LTV:CAC ratio determines if your business model is sustainable or a death spiral." data-id="element-80" />
      <div className="space-y-2" data-id="element-81">
        <label className="flex items-center text-sm text-gray-300" data-id="element-82">
          Average Annual Revenue Per User (ARPU)
          <Tooltip title="ARPU - Your Revenue Engine" description="How much does each customer pay you per year? This needs to be significantly higher than your CAC. Industry benchmark: aim for 3:1 LTV:CAC ratio minimum. Enterprise products can charge $10k-$100k+. SMB products typically $1k-$10k. Consumer products $100-$1k." learnMoreUrl="https://www.reforge.com/blog/how-to-build-a-growth-model" data-id="element-83" />
        </label>
        <div className="relative" data-id="element-84">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400" data-id="element-85">
            <DollarSignIcon className="w-4 h-4" data-id="element-86" />
          </span>
          <input type="text" inputMode="numeric" data-int-field="true" defaultValue={formatInt(arpu)} onChange={handleArpuChange} onFocus={handleIntInputFocus} onBlur={handleIntInputBlur} className="w-full bg-[#0F172A] border border-[#334155] text-white py-2 pl-10 pr-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-mono" data-id="element-87" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4" data-id="element-88">
        <div className="bg-[#0F172A] bg-opacity-70 p-4 rounded-lg" data-id="element-89">
          <div className="text-sm text-gray-400 mb-1 flex items-center" data-id="element-90">
            Estimated CAC
            <Tooltip title="Customer Acquisition Cost" description="How much you spend to acquire one customer through your chosen channel. This includes marketing, sales, and onboarding costs. Lower is better, but it must match your product type." data-id="element-91" />
          </div>
          <div className="text-xl font-mono text-white" data-id="element-92">
            ${cac.toLocaleString()}
          </div>
        </div>
        <div className="bg-[#0F172A] bg-opacity-70 p-4 rounded-lg" data-id="element-93">
          <div className="text-sm text-gray-400 mb-1 flex items-center" data-id="element-94">
            LTV:CAC Ratio
            <Tooltip title="The Growth Sustainability Metric" description="Lifetime Value divided by Customer Acquisition Cost. 3:1 or higher = sustainable growth. 2:1 = breakeven territory. Below 2:1 = losing money on every customer. This is your north star for unit economics." learnMoreUrl="https://www.reforge.com/blog/unit-economics" data-id="element-95" />
          </div>
          <div className={`text-xl font-mono ${ltvCacRatio >= 3 ? 'text-green-500' : ltvCacRatio >= 2 ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-96">
            {ltvCacRatio.toFixed(1)}:1
          </div>
        </div>
      </div>
      {ltvCacRatio < 3 && <div className="p-3 bg-yellow-500 bg-opacity-20 border border-yellow-500 rounded-lg" data-id="element-97">
          <div className="text-xs text-yellow-300 leading-relaxed" data-id="element-98">
            💡 <strong data-id="element-99">Benchmark:</strong> Aim for 3:1 LTV:CAC or higher. Below
            3:1 means you're leaving money on the table or growing
            inefficiently.
          </div>
        </div>}
      {arpu < 1000 && cac > 1000 && <div className="mt-4 p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg" data-id="element-100">
          <div className="flex items-center text-red-500 font-semibold mb-2" data-id="element-101">
            <AlertTriangleIcon className="w-5 h-5 mr-2" data-id="element-102" />
            CRITICAL: ARPU-CAC MISMATCH
          </div>
          <p className="text-sm text-gray-300 mb-3" data-id="element-103">
            Your pricing (${arpu.toLocaleString()}) is too low for your
            acquisition channel (${cac.toLocaleString()} CAC). You're losing
            money on every customer.
          </p>
          <div className="bg-black bg-opacity-30 p-3 rounded-lg" data-id="element-104">
            <div className="text-xs font-semibold text-red-400 mb-2" data-id="element-105">
              Why This Kills Growth:
            </div>
            <ul className="text-xs text-gray-300 space-y-1 list-disc list-inside" data-id="element-106">
              <li data-id="element-107">Every new customer loses you money</li>
              <li data-id="element-108">Growth accelerates your losses</li>
              <li data-id="element-109">You'll run out of runway before reaching profitability</li>
            </ul>
            <div className="text-xs font-semibold text-red-400 mt-3 mb-2" data-id="element-110">
              Solutions:
            </div>
            <ul className="text-xs text-gray-300 space-y-1 list-disc list-inside" data-id="element-111">
              <li data-id="element-112">Increase pricing to match your channel economics</li>
              <li data-id="element-113">Switch to a lower-cost acquisition channel</li>
              <li data-id="element-114">Improve conversion rates to lower effective CAC</li>
            </ul>
          </div>
        </div>}
    </div>;
};